// src/shared/api/apiClient.ts
import axios from 'axios';
import { pipeexRuntimeConfig } from '../config/pipeexRuntimeConfig';
import { useIntegrationDiagnosticsStore } from '../stores/useIntegrationDiagnosticsStore';
import { clearAuthSessionStorage } from '../lib/authSession';
import { makeTraceparent } from './traceparent';

// Create the base axios instance
const apiClient = axios.create({
  baseURL: pipeexRuntimeConfig.apiBaseUrl,
  timeout: 10000, // 10 seconds timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

const authClient = axios.create({
  baseURL: pipeexRuntimeConfig.apiBaseUrl,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

let refreshPromise: Promise<string | null> | null = null;

function decodeUserIdFromToken(token: string): number | null {
  try {
    const payloadPart = token.split('.')[1];
    if (!payloadPart) {
      return null;
    }
    const base64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    const rawId =
      payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] ??
      payload.nameid ??
      payload.sub ??
      payload.userId;
    const parsed = Number(rawId);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = localStorage.getItem('refreshToken');
  const accessToken = localStorage.getItem('accessToken');

  if (!refreshToken || !accessToken) {
    return null;
  }

  let userId: number | null = null;
  const storedUser = localStorage.getItem('user');
  if (storedUser) {
    try {
      const parsed = JSON.parse(storedUser);
      const parsedId = Number(parsed?.id);
      if (Number.isFinite(parsedId)) {
        userId = parsedId;
      }
    } catch {
      userId = null;
    }
  }

  if (userId === null) {
    userId = decodeUserIdFromToken(accessToken);
  }

  if (userId === null) {
    return null;
  }

  try {
    const response = await authClient.post<{ accessToken: string; refreshToken: string }>(
      '/auth/refresh-token',
      {
        userId,
        refreshToken,
      }
    );

    const nextAccessToken = response.data?.accessToken;
    const nextRefreshToken = response.data?.refreshToken;
    if (!nextAccessToken || !nextRefreshToken) {
      return null;
    }

    localStorage.setItem('accessToken', nextAccessToken);
    localStorage.setItem('refreshToken', nextRefreshToken);
    return nextAccessToken;
  } catch (err: any) {
    // Distinguish server rejection (401/403 — token truly expired) from
    // network errors (server unreachable). Only return null (which triggers
    // session clear) for server rejections; for network errors, return the
    // existing token so the session isn't wiped while offline.
    const status = err?.response?.status;
    if (status === 401 || status === 403) {
      console.warn('[apiClient] Refresh token rejected by server', status);
      return null;
    }
    console.warn('[apiClient] Refresh failed (network/timeout), keeping session', err?.message);
    return localStorage.getItem('accessToken');
  }
}

// Request interceptor to add auth token if available
apiClient.interceptors.request.use(
  (config: any) => {
    // Prefer pipeex token keys, keep legacy fallback for compatibility.
    const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: any) => {
    return Promise.reject(error);
  }
);

// W3C traceparent injection. F4 of epic
// 2026-05-26_export-flow-and-observability, FR-2 — every outbound REST
// call carries `traceparent: 00-<32hex>-<16hex>-01` so the API's
// AspNetCoreInstrumentation continues the trace, and the legacy
// `X-Trace-Id` header for backwards compatibility with diagnostic
// dashboards that only grep for that name. Both headers reference the
// SAME 32-hex trace-id so cross-correlation is unambiguous.
//
// Caller-supplied headers (e.g. runExportFlow's own traceparent for
// the /pcf-runs call) win — we only fill in the blanks.
apiClient.interceptors.request.use(
  (config: any) => {
    if (!config.headers) {
      config.headers = {};
    }
    const headers = config.headers;
    const existing = headers.traceparent || headers.Traceparent;
    let traceparentValue: string;
    if (existing && /^00-[0-9a-f]{32}-[0-9a-f]{16}-[0-9a-f]{2}$/i.test(existing)) {
      traceparentValue = existing;
    } else {
      traceparentValue = makeTraceparent();
      headers.traceparent = traceparentValue;
    }
    if (!headers['X-Trace-Id']) {
      // Derive the trace-id portion from the just-set traceparent so
      // both headers always agree.
      headers['X-Trace-Id'] = traceparentValue.split('-')[1];
    }
    return config;
  },
  (error: any) => Promise.reject(error)
);

// Response interceptor to handle common errors
apiClient.interceptors.response.use(
  (response: any) => {
    useIntegrationDiagnosticsStore.getState().setLastApiError(null);
    return response;
  },
  (error: any) => {
    const originalRequest = error?.config || {};

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !String(originalRequest.url || '').includes('/auth/refresh-token') &&
      !String(originalRequest.url || '').includes('/auth/login')
    ) {
      originalRequest._retry = true;

      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
      }

      return refreshPromise.then((newToken) => {
        if (!newToken) {
          clearAuthSessionStorage();
          return Promise.reject(error);
        }

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      });
    }

    if (error.response?.status === 401) {
      clearAuthSessionStorage();
      console.error('Unauthorized access - session expired');
    } else if (error.response?.status === 403) {
      console.error('Forbidden access');
    } else if (error.response?.status === 500) {
      console.error('Server error');
    }

    const method = String(error?.config?.method || 'GET').toUpperCase();
    const url = String(error?.config?.url || 'unknown-url');
    const status = error?.response?.status ?? 'n/a';
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      (typeof error?.response?.data === 'string' ? error.response.data : null) ||
      error?.message ||
      'API request failed';
    useIntegrationDiagnosticsStore
      .getState()
      .setLastApiError(`[${method}] ${url} -> ${status}: ${message}`);

    return Promise.reject(error);
  }
);

export default apiClient;
